// i18n-Loader für dunklesloch (Card #2092).
//
// API:
//   initI18n()               — initialisiert (gespeichert > navigator.language > en)
//   useSystemLang()          — zwingt Device-Sprache
//   t(key, params?)          — aktive Sprache, Fallback en
//   setLang(code)            — persistiert in localStorage, feuert Listener
//   getLang()                — aktive Sprache
//   onLangChange(cb)         — Listener
//   getSortedLangs()         — sortiert nach Sprachverwandtschaft (Phase 0.5)
//   getLangName(code)        — Native-Name einer Sprache
//   FLAG                     — Flaggen-Emoji pro Sprache
//
// Bundles werden statisch per import geladen (Vite bundelt sie inline).

import en from "./lang/en.json";
import de from "./lang/de.json";
import es from "./lang/es.json";
import fr from "./lang/fr.json";
import zh from "./lang/zh.json";
import ja from "./lang/ja.json";
import ko from "./lang/ko.json";
import hi from "./lang/hi.json";
import idJson from "./lang/id.json";
import vi from "./lang/vi.json";
import th from "./lang/th.json";

export type LangCode = "en" | "de" | "es" | "fr" | "zh" | "ja" | "ko" | "hi" | "id" | "vi" | "th";

export const LANGS: readonly LangCode[] = [
  "en", "de", "es", "fr", "zh", "ja", "ko", "hi", "id", "vi", "th",
];

// `id` ist reserviertes Wort in TS, daher `idJson` beim Import.
export const BUNDLES: Record<LangCode, Record<string, string>> = {
  en: en as unknown as Record<string, string>,
  de: de as unknown as Record<string, string>,
  es: es as unknown as Record<string, string>,
  fr: fr as unknown as Record<string, string>,
  zh: zh as unknown as Record<string, string>,
  ja: ja as unknown as Record<string, string>,
  ko: ko as unknown as Record<string, string>,
  hi: hi as unknown as Record<string, string>,
  id: idJson as unknown as Record<string, string>,
  vi: vi as unknown as Record<string, string>,
  th: th as unknown as Record<string, string>,
};

/** Expose für Tests (gleiches Objekt). */
export const BUNDLES_FOR_TESTS = BUNDLES;

const STORAGE_KEY = "dunklesloch.lang";
const FALLBACK: LangCode = "en";

let _active: LangCode = FALLBACK;
const _listeners = new Set<(l: LangCode) => void>();

/** Locale → unterstützter Code. */
export function mapSystemLang(raw: string): LangCode {
  const l = (raw || "").toLowerCase().replace("_", "-");
  if (l.startsWith("zh")) return "zh";
  if (l.startsWith("ja")) return "ja";
  if (l.startsWith("ko")) return "ko";
  if (l.startsWith("hi")) return "hi";
  if (l.startsWith("id") || l.startsWith("ms")) return "id";
  if (l.startsWith("vi")) return "vi";
  if (l.startsWith("th")) return "th";
  const m = /^([a-z]{2})(-|$)/.exec(l);
  const code = m ? m[1] : "";
  const known = ["de", "en", "fr", "es", "it", "pt", "nl", "pl", "tr", "ru", "ar", "cs", "sv", "da", "fi", "no", "el", "he"];
  if (known.includes(code)) return code as LangCode;
  return "en";
}

/** Aktive Sprache. */
export function getLang(): LangCode {
  return _active;
}

/** Setzt eine neue Sprache. Persistiert. Feuert Listener. */
export function setLang(lang: LangCode): void {
  if (!BUNDLES[lang]) lang = FALLBACK;
  if (lang === _active) return;
  _active = lang;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode */
  }
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  for (const l of _listeners) l(lang);
}

/** Listener; gibt Unsubscribe zurück. */
export function onLangChange(cb: (l: LangCode) => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

/** Liest navigator.language, mappt, setzt Sprache. */
export function useSystemLang(): LangCode {
  const raw = typeof navigator !== "undefined"
    ? (navigator.language || (navigator.languages && navigator.languages[0]) || "")
    : "";
  const detected = mapSystemLang(raw);
  setLang(detected);
  return _active;
}

/** Initialisiert: gespeicherter Wert > navigator.language > en. */
export function initI18n(): LangCode {
  let saved: LangCode | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && (LANGS as readonly string[]).includes(raw)) saved = raw as LangCode;
  } catch {
    /* private mode */
  }
  if (saved) {
    _active = saved;
    if (typeof document !== "undefined") document.documentElement.lang = saved;
    return saved;
  }
  return useSystemLang();
}

/** Übersetzt. Lookup: aktive Sprache → en → key. */
export function t(key: string, params?: Record<string, string | number>): string {
  const fromActive = BUNDLES[_active]?.[key];
  if (fromActive) return substitute(fromActive, params);
  const fromEn = BUNDLES.en?.[key];
  if (fromEn) return substitute(fromEn, params);
  return key;
}

function substitute(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m);
}

/** Liefert alle Bundle-Keys (für Validierung). */
export function allKeys(): string[] {
  return Object.keys(BUNDLES.en);
}

// ─────────────── Phase 0.5: Sortierung nach Sprachverwandtschaft ───────────────

const FAMILY_CJK = new Set(["zh", "ja", "ko"]);
const FAMILY_LATIN_EU = new Set(["de", "es", "fr", "it", "pt", "nl", "pl", "tr", "ru"]);
const FAMILY_SOUTHEAST_ASIA = new Set(["id", "vi", "th", "ms"]);
const FAMILY_SOUTH_ASIA = new Set(["hi"]);

/** Sortiert Sprachen nach Verwandtschaft. HARTE REGEL: alle bleiben. */
export function getSortedLangsForRegion(
  rawLocale: string,
  allLangs: readonly LangCode[]
): LangCode[] {
  if (!allLangs || allLangs.length === 0) return [];
  const region = mapSystemLang(rawLocale);
  const input = [...allLangs];

  const native =
    region && region !== "en" && input.includes(region) ? [region as LangCode] : [];
  const english = input.includes("en") ? ["en" as LangCode] : [];

  let familyOrder: LangCode[] = [];
  if (FAMILY_CJK.has(region)) {
    const order: LangCode[] = ["zh", "ja", "ko"];
    familyOrder = order.filter((c) => c !== region && input.includes(c));
  } else if (FAMILY_LATIN_EU.has(region)) {
    const order: LangCode[] = ["de", "es", "fr"];
    familyOrder = order.filter((c) => c !== region && input.includes(c));
  } else if (FAMILY_SOUTHEAST_ASIA.has(region)) {
    const order: LangCode[] = ["id", "vi", "th"];
    familyOrder = order.filter((c) => c !== region && input.includes(c));
  } else if (FAMILY_SOUTH_ASIA.has(region)) {
    familyOrder = [];
  }

  const taken = new Set([...native, ...english, ...familyOrder]);
  const rest = input.filter((l) => !taken.has(l));

  return [...native, ...english, ...familyOrder, ...rest];
}

export function getDefaultLangForRegion(
  rawLocale: string,
  allLangs: readonly LangCode[]
): LangCode {
  const sorted = getSortedLangsForRegion(rawLocale, allLangs);
  return sorted[0] ?? "en";
}

/** Sortiert die globalen LANGS nach aktueller navigator.language. */
export function getSortedLangs(): LangCode[] {
  if (typeof navigator === "undefined") return [...LANGS];
  return getSortedLangsForRegion(navigator.language, LANGS);
}

/** Anzeige-Name einer Sprache (Native-Name). */
export function getLangName(code: LangCode): string {
  return t(`language_${code}`);
}

/** Aktualisiert DOM-Texte mit [data-i18n]-Attributen. */
export function applyTranslations(root: ParentNode = document): void {
  const els = root.querySelectorAll<HTMLElement>(
    "[data-i18n], [data-i18n-html], [data-i18n-aria], [data-i18n-title], [data-i18n-placeholder]"
  );
  for (const el of Array.from(els)) {
    const txt = el.dataset.i18n;
    if (txt) el.textContent = t(txt);
    const html = el.dataset.i18nHtml;
    if (html) el.innerHTML = t(html);
    const aria = el.dataset.i18nAria;
    if (aria) el.setAttribute("aria-label", t(aria));
    const ttl = el.dataset.i18nTitle;
    if (ttl) el.setAttribute("title", t(ttl));
    const ph = el.dataset.i18nPlaceholder;
    if (ph) (el as HTMLInputElement).placeholder = t(ph);
  }
  const titleEl = root.querySelector("title");
  if (titleEl && titleEl.dataset.i18n) titleEl.textContent = t(titleEl.dataset.i18n);
  const metaDesc = root.querySelector<HTMLMetaElement>("meta[name='description'][data-i18n]");
  if (metaDesc && metaDesc.dataset.i18n) metaDesc.setAttribute("content", t(metaDesc.dataset.i18n));
}

// Flag-Emoji-Set für die Sprachauswahl.
export const FLAG: Record<LangCode, string> = {
  en: "🇬🇧",
  de: "🇩🇪",
  es: "🇪🇸",
  fr: "🇫🇷",
  zh: "🇨🇳",
  ja: "🇯🇵",
  ko: "🇰🇷",
  hi: "🇮🇳",
  id: "🇮🇩",
  vi: "🇻🇳",
  th: "🇹🇭",
};
